import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Competition, CompetitionDto} from '../model/competition/Competition';
import {User, UserDto} from '../model/auth/User';

@Injectable({
  providedIn: 'root'
})
export class CompetitionService {

  constructor(private http: HttpClient) {
  }

  public static COMPETITION_ID = 'COMPETITION_ID';
  private static HOST_ID = 'HOST_ID';
  private static DOMAIN_ID = 'DOMAIN_ID';

  private static COMPETITIONS_URL = '/competitions';
  private static COMPETITIONS_DOMAIN_DETAILS_URL = `${CompetitionService.COMPETITIONS_URL}/domains/${CompetitionService.DOMAIN_ID}`;
  public static COMPETITIONS_DETAILS_URL = `${CompetitionService.COMPETITIONS_URL}/${CompetitionService.COMPETITION_ID}`;
  private static COMPETITIONS_HOSTS_URL = `${CompetitionService.COMPETITIONS_DETAILS_URL}/hosts`;
  private static COMPETITIONS_CONTACT_HOSTS_URL = `${CompetitionService.COMPETITIONS_HOSTS_URL}/contact`;
  private static COMPETITIONS_HOST_DETAILS_URL = `${CompetitionService.COMPETITIONS_HOSTS_URL}/${CompetitionService.HOST_ID}`;
  private static COMPETITIONS_HOST_CONTACT_URL = `${CompetitionService.COMPETITIONS_HOST_DETAILS_URL}/contact`;

  getCompetitions(available = false): Observable<Competition[]> {
    const url = CompetitionService.COMPETITIONS_URL + (available ? `?available=${available}` : '');
    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as CompetitionDto[]).map(item => Competition.parse(item)))
      );
  }

  getDetails(id: string): Observable<Competition> {
    const url = CompetitionService.COMPETITIONS_DETAILS_URL.replace(CompetitionService.COMPETITION_ID, id);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => Competition.parse(data as CompetitionDto))
      );
  }

  getDomainDetails(domain: string): Observable<Competition> {
    domain = domain.trim().toLowerCase();
    const url = CompetitionService.COMPETITIONS_DOMAIN_DETAILS_URL.replace(CompetitionService.DOMAIN_ID, domain);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => Competition.parse(data as CompetitionDto))
      );
  }

  getHosts(competitionId: string): Observable<User[]> {
    const url = CompetitionService.COMPETITIONS_HOSTS_URL.replace(CompetitionService.COMPETITION_ID, competitionId);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as UserDto[]).map(item => User.parse(item)))
      );
  }

  getContactHosts(competitionId: string): Observable<User[]> {
    const url = CompetitionService.COMPETITIONS_CONTACT_HOSTS_URL.replace(CompetitionService.COMPETITION_ID, competitionId);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as UserDto[]).map(item => User.parse(item)))
      );
  }

  updateContactHost(competitionId: string, userId: string, contact: boolean): Observable<User> {
    const url = CompetitionService.COMPETITIONS_HOST_CONTACT_URL
      .replace(CompetitionService.COMPETITION_ID, competitionId)
      .replace(CompetitionService.HOST_ID, userId);

    return this.http.post(`${environment.api.host}${url}`, {contact_host: contact}).pipe(
      map(data => User.parse(data as UserDto))
    );
  }

  deleteHost(competitionId: string, userId: string): Observable<object> {
    const url = CompetitionService.COMPETITIONS_HOST_DETAILS_URL
      .replace(CompetitionService.COMPETITION_ID, competitionId)
      .replace(CompetitionService.HOST_ID, userId);

    return this.http.delete(`${environment.api.host}${url}`);
  }

  assignHost(competitionId: string, user: User): Observable<object> {
    const url = CompetitionService.COMPETITIONS_HOSTS_URL.replace(CompetitionService.COMPETITION_ID, competitionId);
    const data: UserDto = {
      id: user.id,
      email: user.email
    };
    if (user.firstName) data.first_name = user.firstName;
    if (user.lastName) data.last_name = user.lastName;

    return this.http.post(`${environment.api.host}${url}`, data);
  }

  create(name: string, domain: string): Observable<Competition> {
    const data: CompetitionDto = {
      name: name,
      domain: domain
    };

    return this.http.post(`${environment.api.host}${CompetitionService.COMPETITIONS_URL}`, data)
      .pipe(
        map(data => Competition.parse(data as CompetitionDto))
      );
  }

  update(id: string, name?: string, domain?: string, description?: string, published?: boolean): Observable<Competition> {
    const data: CompetitionDto = {};
    if (name) data.name = name;
    if (domain) data.domain = domain;
    if (description) data.description = description;
    if (published) data.published = published;

    const url = CompetitionService.COMPETITIONS_DETAILS_URL.replace(CompetitionService.COMPETITION_ID, id);

    return this.http.patch(`${environment.api.host}${url}`, data)
      .pipe(
        map(data => Competition.parse(data as CompetitionDto))
      );
  }

  delete(id: string): Observable<any> {
    const url = CompetitionService.COMPETITIONS_DETAILS_URL.replace(CompetitionService.COMPETITION_ID, id);

    return this.http.delete(`${environment.api.host}${url}`);
  }

}
